// src/modules/tickets/pages/TicketMiniLabelView.tsx
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Button,
  Typography,
  Divider,
  Stack,
  Avatar,
  Skeleton,
  useTheme,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon, Print as PrintIcon } from "@mui/icons-material";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";

type Ticket = {
  _id: string;
  ticketNumber: string;
  clientName?: string;
  brand?: string;
  model?: string;
  jobType?: string;
  createdAt: string;
};

type Shop = {
  tenantName?: string;
  logoURL?: string;
};

export default function TicketMiniLabelView() {
  useProtectedRoute();
  const { token } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [shop, setShop] = useState<Shop | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [ticketRes, shopRes] = await Promise.all([
          api.get(`/tickets/${id}`, { headers }),
          api.get(`/shops/me`, { headers }),
        ]);
        if (!mounted) return;
        setTicket(ticketRes.data);
        setShop(shopRes.data);
      } catch {
        // soft-fail UI
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchData();
    return () => {
      mounted = false;
    };
  }, [id, token]);

  const line = (label: string, value?: string | number) => (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ ml: 1, maxWidth: 180 }} noWrap>
        {value ? String(value) : "—"}
      </Typography>
    </Stack>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: (t) => (t.palette.mode === "light" ? "#f4f6f8" : "background.default"),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        "@media print": {
          bgcolor: "#fff",
          p: 0,
          minHeight: "auto",
        },
      }}
    >
      {/* Outer card sized like a mini label */}
      <Paper
        elevation={4}
        sx={{
          width: 320, // ~80mm
          borderRadius: 2,
          border: (t) => `1px solid ${t.palette.divider}`,
          overflow: "hidden",
          boxShadow: 3,
          "@media print": {
            m: 0,
            width: "auto", // let printer handle width
            boxShadow: "none",
            border: "1px solid #000",
          },
        }}
      >
        {/* Top control bar (hidden when printing) */}
        <Box
          className="no-print"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
            p: 1,
            bgcolor: (t) => (t.palette.mode === "light" ? "#eef2ff" : t.palette.primary.dark),
            borderBottom: (t) => `1px solid ${t.palette.divider}`,
            "@media print": { display: "none" },
          }}
        >
          <Button
            size="small"
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/tickets/${id}`)}
            sx={{ fontWeight: 700 }}
          >
            Back
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
            sx={{ fontWeight: 800 }}
          >
            Print
          </Button>
        </Box>

        {/* Slim brand band to match app color */}
        <Box
          sx={{
            height: 4,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            "@media print": {
              background: "#000",
            },
          }}
        />

        <Box sx={{ p: 1.5 }}>
          {/* Header: Logo + Shop name */}
          <Stack alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
            {loading ? (
              <Skeleton variant="circular" width={40} height={40} />
            ) : shop?.logoURL ? (
              <Avatar
                src={shop.logoURL}
                alt="Shop Logo"
                variant="rounded"
                sx={{
                  width: 40,
                  height: 40,
                  border: (t) => `1px solid ${t.palette.divider}`,
                  bgcolor: "transparent",
                }}
                imgProps={{ style: { objectFit: "contain" } }}
              />
            ) : (
              <Avatar
                variant="rounded"
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: "primary.main",
                  fontWeight: 800,
                }}
              >
                {(shop?.tenantName || "R").slice(0, 1).toUpperCase()}
              </Avatar>
            )}

            {loading ? (
              <Skeleton width={160} height={18} />
            ) : (
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 900, letterSpacing: 0.4, textTransform: "uppercase", lineHeight: 1 }}
                noWrap
              >
                {shop?.tenantName || "RepairSync"}
              </Typography>
            )}
            <Typography
              variant="overline"
              sx={{ fontSize: 9, letterSpacing: 1, opacity: 0.7, lineHeight: 1 }}
            >
              REPAIR TAG — REPAIRSYNC
            </Typography>
          </Stack>

          <Divider sx={{ mb: 1 }} />

          {/* Ticket number big + date */}
          <Stack spacing={0.25} sx={{ mb: 0.75 }}>
            {loading ? (
              <>
                <Skeleton width={180} />
                <Skeleton width={120} />
              </>
            ) : (
              <>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 900, letterSpacing: 0.3, lineHeight: 1.1 }}
                  noWrap
                >
                  Ticket #{ticket?.ticketNumber}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {ticket?.createdAt ? new Date(ticket.createdAt).toLocaleDateString() : "—"}
                </Typography>
              </>
            )}
          </Stack>

          {/* Info lines */}
          {loading ? (
            <Stack spacing={0.5}>
              <Skeleton height={16} />
              <Skeleton height={16} />
              <Skeleton height={16} />
              <Skeleton height={16} />
              <Skeleton height={16} />
            </Stack>
          ) : (
            <Stack spacing={0.35} sx={{ fontSize: 11, lineHeight: 1 }}>
              {line("Client", ticket?.clientName)}
              {line("Brand", ticket?.brand)}
              {line("Model", ticket?.model)}
              {line("Job", ticket?.jobType)}
            </Stack>
          )}
        </Box>

        {/* Bottom border to frame the label when cut */}
        <Box
          sx={{
            height: 3,
            bgcolor: (t) => (t.palette.mode === "light" ? "#e5e7eb" : t.palette.divider),
            "@media print": { bgcolor: "#000" },
          }}
        />
      </Paper>
    </Box>
  );
}

